// redux/slices/gallerySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { headers } from "../apiConfig";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

// helper: абсолютный URL + cache-busting (для public / при желании можно и для приватных)
const toAbs = (p) => {
  if (!p) return p;
  if (/^https?:\/\//i.test(p)) return p;
  const base = (getServerUrl() || "").replace(/\/$/, "");
  if (!base) return p;
  const path = String(p).replace(/^\//, "");
  return `${base}/${path}`;
};

const stripContentType = (h = {}) => {
  const copy = { ...h };
  Object.keys(copy).forEach((k) => {
    if (k.toLowerCase() === "content-type") delete copy[k];
  });
  return copy;
};

/* ============================
   fetchGalleryImages (private)
   ============================ */

export const fetchGalleryImages = createAsyncThunk(
  "gallery/fetchImages",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const authHeaders = await getAuthHeaders();

      const response = await axios.get(`${baseUrl}/gallery/${uid}/gallery`, {
        headers: authHeaders,
        withCredentials: true,
        validateStatus: () => true,
      });

      if (response.status === 401) {
        throw new Error("Unauthorized (401) при загрузке галереи");
      }

      if (response.status === 404) {
        // нет фоток — нормальная ситуация
        return [];
      }

      if (response.status !== 200) {
        throw new Error(
          response.data?.message ||
          `Ошибка загрузки изображений (HTTP ${response.status})`
        );
      }

      const images = response.data;
      if (!Array.isArray(images)) return [];
      // здесь оставляю как есть (без toAbs) — ты уже сам знаешь,
      // как их отображаешь (через ImageWithFallback и getServerUrl)
      return images;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Ошибка загрузки изображений";
      return rejectWithValue(msg);
    }
  }
);

/* ============================
   uploadGalleryImages (private)
   ============================ */

export const uploadGalleryImages = createAsyncThunk(
  "gallery/uploadGalleryImages",
  async ({ uid, images }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const formData = new FormData();
      formData.append("uid", uid);

      (images || []).forEach((img, index) => {
        if (img instanceof File || img instanceof Blob) {
          const name = img.name || `image_${index}.jpg`;
          formData.append(`file_${index}`, img, name);
        } else if (img && typeof img === "object" && img.uri) {
          const name = img.name || img.uri.split("/").pop() || `image_${index}.jpg`;
          const type = img.type || "image/jpeg";
          formData.append(`file_${index}`, { uri: img.uri, name, type });
        } else {
          console.warn("Unsupported image payload", img);
        }
      });

      const baseHeaders = await getAuthHeaders();
      const safeHeaders = stripContentType(baseHeaders); // не ставим content-type вручную

      const resp = await axios.post(`${baseUrl}/gallery`, formData, {
        headers: safeHeaders,
        withCredentials: true,
        validateStatus: () => true,
      });

      if (resp.status === 401) {
        throw new Error("Unauthorized (401) при загрузке изображений");
      }

      if (resp.status !== 200 && resp.status !== 201) {
        throw new Error(
          resp.data?.message ||
          `Ошибка загрузки изображений (HTTP ${resp.status})`
        );
      }

      // ожидаем resp.data.filePaths — массив путей
      const filePaths = Array.isArray(resp.data?.filePaths)
        ? resp.data.filePaths
        : [];

      return filePaths;
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Upload failed";
      console.error("Upload failed:", msg, err?.response);
      return rejectWithValue(msg);
    }
  }
);

/* ============================
   deleteGalleryImages (private)
   ============================ */

export const deleteGalleryImages = createAsyncThunk(
  "gallery/deleteGalleryImages",
  async ({ uid, imagePath }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      if (!imagePath) throw new Error("imagePath не передан");

      const baseUrl = getServerUrl();
      if (!baseUrl) throw new Error("API server URL is not configured");

      const authHeaders = await getAuthHeaders();

      // если в state у тебя уже абсолютный URL, убираем baseURL и query
      const localPath = imagePath.replace(baseUrl, "").split("?")[0];

      const resp = await axios.delete(`${baseUrl}/gallery/delete`, {
        data: { uid, imagePath: localPath },
        headers: authHeaders,
        withCredentials: true,
        validateStatus: () => true,
      });

      if (resp.status === 401) {
        throw new Error("Unauthorized (401) при удалении изображения");
      }

      if (resp.status !== 200) {
        throw new Error(
          resp.data?.message ||
          `Ошибка удаления изображения (HTTP ${resp.status})`
        );
      }

      return imagePath;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Ошибка удаления изображения";
      return rejectWithValue(msg);
    }
  }
);

/* ============================
   fetchPublicGalleryImages
   ============================ */

export const fetchPublicGalleryImages = createAsyncThunk(
  "gallery/fetchPublicImages",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const base = (getServerUrl() || "").replace(/\/$/, "");
      if (!base) throw new Error("API server URL is not configured");

      const { data } = await axios.get(`${base}/public/${uid}/gallery`, {
        validateStatus: () => true,
      });

      if (Array.isArray(data)) {
        // нормализуем в абсолютные http-URL
        return data.map(toAbs);
      }
      return [];
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Ошибка загрузки (public)";
      return rejectWithValue(msg);
    }
  }
);

/* ============================
   slice
   ============================ */

const initialState = {
  images: [],
  status: "idle",
  error: null,
};

const gallerySlice = createSlice({
  name: "gallery",
  initialState,
  reducers: {
    clearGallery(state) {
      state.images = [];
      state.status = "idle";
      state.error = null;
      state.lastUid = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch (private)
      .addCase(fetchGalleryImages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchGalleryImages.fulfilled, (state, action) => {
        state.status = "idle";
        state.images = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchGalleryImages.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "Fetch failed";
      })

      // upload
      .addCase(uploadGalleryImages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadGalleryImages.fulfilled, (state, action) => {
        state.status = "idle";
        const paths = Array.isArray(action.payload) ? action.payload : [];
        state.images.push(...paths);
      })
      .addCase(uploadGalleryImages.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "Upload failed";
      })

      // delete
      .addCase(deleteGalleryImages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteGalleryImages.fulfilled, (state, action) => {
        state.status = "idle";
        const removed = action.payload;
        state.images = state.images.filter((image) => image !== removed);
      })
      .addCase(deleteGalleryImages.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "Delete failed";
      })

      // public
      .addCase(fetchPublicGalleryImages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicGalleryImages.fulfilled, (state, action) => {
        state.status = "idle";
        state.images = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPublicGalleryImages.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "Ошибка загрузки (public)";
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export const { clearGallery } = gallerySlice.actions;
export default gallerySlice.reducer;
